import {
  Button,
  Card,
  CardHeader,
  CheckboxField,
  EmailInput,
  FormField,
  InlineAlert,
  PhoneInput,
  normalizeUsPhoneNumber,
} from "@constructorfleet/ui/design-system";
import {
  NOTIFICATION_CHANNEL_OPTIONS,
  type BhgCallDashboardProvider,
  type BhgSubscriptionInfo,
  type NotificationChannel,
} from "../types";
import { useEffect } from "react";
import { Link } from "react-router-dom";

const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  sms: "SMS",
  email: "Email",
};

export function getSelectedNotificationFieldState(selectedChannels: NotificationChannel[]) {
  return {
    smsSelected: selectedChannels.includes("sms"),
    emailSelected: selectedChannels.includes("email"),
  };
}

function getNotificationInputLayoutClass(input: {
  smsSelected: boolean;
  emailSelected: boolean;
  hasAccountPhone: boolean;
  hasAccountEmail: boolean;
}) {
  const { smsSelected, emailSelected, hasAccountPhone, hasAccountEmail } = input;

  if (emailSelected && !smsSelected && hasAccountEmail) {
    return "notification-inputs-layout notification-inputs-layout--email-only";
  }

  if (smsSelected && !emailSelected && !hasAccountPhone) {
    return "notification-inputs-layout notification-inputs-layout--sms-only";
  }

  if (smsSelected && emailSelected && !hasAccountPhone && hasAccountEmail) {
    return "notification-inputs-layout notification-inputs-layout--phone-email-split";
  }

  return "notification-inputs-layout notification-inputs-layout--stack";
}

export type BhgNotificationSettingsMessages = {
  phoneNumber?: string;
  email?: string;
  notificationChannels?: string;
  consentToMessages?: string;
  provider?: string;
};

export interface BhgNotificationSettingsProps {
  onNotificationSettingsUpdated?: (settings: {
    notificationChannels: NotificationChannel[];
  }) => void;
  provider?: BhgCallDashboardProvider;
  disableInitialSync?: boolean;
  /** Account-level phone pre-populated from account creation / user profile */
  accountPhone?: string;
  /** Account-level email pre-populated from account creation / user profile */
  accountEmail?: string;
  availableChannels?: NotificationChannel[];
  currentPhone: string;
  setCurrentPhone: (phone: string) => void;
  selectedPhone: string;
  setSelectedPhone: (phone: string) => void;
  currentEmail: string;
  setCurrentEmail: (email: string) => void;
  selectedEmail: string;
  setSelectedEmail: (email: string) => void;
  currentChannels: NotificationChannel[];
  setCurrentChannels: (channels: NotificationChannel[]) => void;
  selectedChannels: NotificationChannel[];
  setSelectedChannels: (channels: NotificationChannel[]) => void;
  currentConsent: boolean;
  setCurrentConsent: (consent: boolean) => void;
  consentToMessages: boolean;
  setConsentToMessages: (consent: boolean) => void;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  message: BhgNotificationSettingsMessages;
  setMessage: React.Dispatch<React.SetStateAction<BhgNotificationSettingsMessages>>;
}

function getAvailableChannels(channels?: NotificationChannel[]): NotificationChannel[] {
  if (!channels) {
    return [...NOTIFICATION_CHANNEL_OPTIONS];
  }
  const unique = [...new Set(channels)];
  return unique.filter((channel) => NOTIFICATION_CHANNEL_OPTIONS.includes(channel));
}

export function filterNotificationChannelsByAvailability(
  selected: NotificationChannel[],
  availableChannels?: NotificationChannel[]
): NotificationChannel[] {
  const allowed = getAvailableChannels(availableChannels);
  if (allowed.length === 0) {
    return [];
  }
  const allowedSet = new Set(allowed);
  return selected.filter((channel) => allowedSet.has(channel));
}

export function BhgNotificationSettings(props: BhgNotificationSettingsProps) {
  const {
    onNotificationSettingsUpdated,
    provider,
    accountPhone,
    accountEmail,
    availableChannels,
    disableInitialSync,
  } = props;
  const allowedChannels = getAvailableChannels(availableChannels);

  const {
    setCurrentPhone,
    setSelectedPhone,
    setCurrentEmail,
    setSelectedEmail,
    setCurrentChannels,
    setSelectedChannels,
    setCurrentConsent,
    setConsentToMessages,
    setSaving,
    setMessage,
  } = props;

  const {
    currentPhone,
    selectedPhone,
    currentEmail,
    selectedEmail,
    currentChannels,
    selectedChannels,
    currentConsent,
    consentToMessages,
    saving,
    message,
  } = props;

  const onPhoneNumberValidation = (isValid: boolean) => {
    const phoneMessage = isValid ? undefined : "Please enter a valid US phone number.";
    const channelMessage =
      selectedChannels.includes("sms") && !isValid
        ? "A valid phone number is required if SMS notifications are selected."
        : undefined;
    setMessage((prev) => ({
      ...prev,
      phoneNumber: phoneMessage,
      notificationChannels: channelMessage,
    }));
  };

  const onEmailValidation = (isValid: boolean) => {
    const emailMessage = isValid ? undefined : "Please enter a valid email address.";
    const channelMessage =
      selectedChannels.includes("email") && !isValid
        ? "A valid email address is required if email notifications are selected."
        : undefined;
    setMessage((prev) => ({ ...prev, email: emailMessage, notificationChannels: channelMessage }));
  };

  const onChannelsChanged = (next: string[]) => {
    if (allowedChannels.length === 0) {
      setSelectedChannels([]);
      setMessage((prev) => ({
        ...prev,
        notificationChannels: "Notification channels are currently disabled by your administrator.",
      }));
      return;
    }
    const normalized = next
      .map((value) => value.trim().toLowerCase())
      .filter((value): value is NotificationChannel =>
        NOTIFICATION_CHANNEL_OPTIONS.includes(value as NotificationChannel)
      );
    const unique = filterNotificationChannelsByAvailability(
      [...new Set(normalized)],
      allowedChannels
    );
    if (unique.length === 0) {
      setMessage((prev) => ({
        ...prev,
        notificationChannels: "Select at least one notification channel.",
      }));
      return;
    }
    const channelMessages: string[] = [];
    if (unique.includes("sms") && !selectedPhone.trim()) {
      channelMessages.push("A phone number is required if SMS notifications are selected.");
    }
    if (unique.includes("email") && !selectedEmail.trim()) {
      channelMessages.push("An email address is required if email notifications are selected.");
    }
    setMessage((prev) => ({
      ...prev,
      notificationChannels: channelMessages.length > 0 ? channelMessages.join(" ") : undefined,
      phoneNumber: unique.includes("sms") ? prev.phoneNumber : undefined,
      email: unique.includes("email") ? prev.email : undefined,
    }));
    setSelectedChannels(unique);
  };

  const onConsentChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const consent = event.target.checked;
    setConsentToMessages(consent);
    setMessage((prev) => ({ ...prev, consentToMessages: undefined }));
  };

  const isValid = Object.values(message ?? {}).every((m) => m === undefined);
  const notificationChannelOptions = allowedChannels.map((value) => ({
    value,
    label: NOTIFICATION_CHANNEL_LABELS[value],
  }));

  useEffect(() => {
    if (disableInitialSync) {
      return;
    }
    let mounted = true;
    void (async () => {
      const [subscription, profile] = await Promise.all([
        provider?.fetchSubscription() ??
          Promise.resolve({
            bhgEnrollment: {
              mobilePhone: currentPhone,
              notificationEmail: currentEmail,
              notificationChannels: currentChannels,
              userId: 0,
              clinicId: null,
              clinicName: "",
              notificationTime: "",
              notificationTimezone: "",
              clinicDosingHours: "",
              clinicOperatingHours: "",
            },
            products: [],
          } as BhgSubscriptionInfo),
        provider?.fetchUserProfile() ??
          Promise.resolve({
            email: currentEmail,
            phoneNumber: currentPhone,
          }),
      ]);
      if (!mounted) return;
      const enrollmentPhone = normalizeUsPhoneNumber(
        subscription?.bhgEnrollment?.mobilePhone ?? ""
      );
      const enrollmentEmail = subscription?.bhgEnrollment?.notificationEmail ?? "";
      const channels = (
        (subscription?.bhgEnrollment?.notificationChannels.length ?? 0) > 0
          ? subscription!.bhgEnrollment!.notificationChannels
          : ["sms"]
      ) as NotificationChannel[];
      const availableForSession = getAvailableChannels(availableChannels);
      const normalizedChannels = filterNotificationChannelsByAvailability(
        channels,
        availableForSession
      );
      const defaultChannels =
        normalizedChannels.length > 0 ? normalizedChannels : availableForSession.slice(0, 1);
      const defaultPhone = enrollmentPhone || normalizeUsPhoneNumber(profile?.phoneNumber ?? "");
      const defaultEmail = enrollmentEmail || (profile?.email ?? "");
      const consentDefault = Boolean(defaultPhone.trim() || defaultEmail.trim());
      onConsentChanged({
        target: { checked: consentDefault },
      } as React.ChangeEvent<HTMLInputElement>);
      onChannelsChanged(defaultChannels);
      setCurrentPhone(enrollmentPhone);
      setSelectedPhone(defaultPhone);
      setCurrentEmail(enrollmentEmail);
      setSelectedEmail(defaultEmail);
      setCurrentChannels(defaultChannels);
      setCurrentConsent(consentDefault);
      setConsentToMessages(consentDefault);
    })();
    return () => {
      mounted = false;
    };
  }, [availableChannels, disableInitialSync]);

  const hasChanged =
    selectedPhone !== currentPhone ||
    selectedEmail !== currentEmail ||
    [...selectedChannels].sort().join(",") !== [...currentChannels].sort().join(",") ||
    consentToMessages !== currentConsent;
  const { smsSelected, emailSelected } = getSelectedNotificationFieldState(selectedChannels);
  const notificationLayoutClassName = getNotificationInputLayoutClass({
    smsSelected,
    emailSelected,
    hasAccountPhone: Boolean(accountPhone),
    hasAccountEmail: Boolean(accountEmail),
  });

  const handleSave = async () => {
    if (!isValid) {
      return;
    }
    if (allowedChannels.length === 0) {
      setMessage((prev) => ({
        ...prev,
        provider: "Notification channels are currently disabled by your administrator.",
      }));
      return;
    }

    setSaving(true);
    setMessage((prev) => ({
      ...prev,
      notificationChannels: undefined,
      phoneNumber: undefined,
      email: undefined,
      consentToMessages: undefined,
    }));
    const ok = await provider?.updateNotifications({
      mobilePhone: normalizeUsPhoneNumber(selectedPhone.trim()),
      notificationEmail: selectedEmail.trim(),
      notificationChannels: selectedChannels,
    });
    setSaving(false);
    if (ok) {
      setCurrentPhone(normalizeUsPhoneNumber(selectedPhone.trim()));
      setCurrentEmail(selectedEmail.trim());
      setCurrentChannels(selectedChannels);
      setCurrentConsent(consentToMessages);
      onNotificationSettingsUpdated?.({
        notificationChannels: selectedChannels,
      });
      setMessage((prev) => ({ ...prev, provider: "Notification channels updated successfully." }));
    } else {
      setMessage((prev) => ({
        ...prev,
        provider: "Failed to update notification channels. Please try again.",
      }));
    }
  };

  return (
    <Card>
      <CardHeader
        title="Notification Settings"
        meta="Choose where BHG call notifications are delivered."
      />
      <div className="card-body ds-stack">
        <p style={{ fontSize: "0.85rem", color: "var(--cf-muted-text)" }}>
          By providing your phone number, you agree to receive SMS BHG schedule update notifications
          from ConstructorFleet. Message frequency may vary. Standard Message and Data Rates may
          apply. Reply STOP to opt out. Reply HELP for help. We will not share mobile information
          with third parties for promotional or marketing purposes. See our{" "}
          <Link to="/privacy">Privacy Policy</Link> and{" "}
          <Link to="/terms">Terms &amp; Conditions</Link> for details.
        </p>
        <div className="notification-selector" style={{ display: "grid", gap: "0.75rem" }}>
          <FormField label="Notification channels" htmlFor="dashboard-notification-channels">
            <div
              id="dashboard-notification-channels"
              role="group"
              aria-label="Notification channels"
              style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
            >
              {notificationChannelOptions.map(({ value, label }) => {
                const isSelected = selectedChannels.includes(value as NotificationChannel);
                return (
                  <Button
                    key={value}
                    type="button"
                    size="sm"
                    variant={isSelected ? "primary" : "secondary"}
                    disabled={saving || allowedChannels.length === 0}
                    aria-pressed={isSelected}
                    onClick={() => {
                      const next = isSelected
                        ? selectedChannels.filter((c) => c !== value)
                        : [...selectedChannels, value as NotificationChannel];
                      onChannelsChanged(next);
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </FormField>
          {allowedChannels.length === 0 ? (
            <InlineAlert tone="danger">
              Notification channels are currently disabled by your administrator.
            </InlineAlert>
          ) : null}
          {message.notificationChannels ? (
            <InlineAlert tone="danger">{message.notificationChannels}</InlineAlert>
          ) : null}
        </div>
        <div className={notificationLayoutClassName}>
          {smsSelected ? (
            <>
              {accountPhone ? (
                <div className="notification-layout-slot notification-layout-slot--phone-toggle">
                  <CheckboxField
                    id="bhg-use-account-phone"
                    checked={selectedPhone === accountPhone}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPhone(accountPhone);
                        onPhoneNumberValidation(true);
                      } else {
                        setSelectedPhone("");
                      }
                    }}
                    label={`Use my account phone number (${accountPhone})`}
                    disabled={saving}
                  />
                </div>
              ) : null}
              <div className="notification-layout-slot notification-layout-slot--phone-input">
                <FormField
                  label="Mobile phone number"
                  htmlFor="dashboard-notification-mobile-phone"
                >
                  <PhoneInput
                    id="dashboard-notification-mobile-phone"
                    value={selectedPhone}
                    valueFormat="e164"
                    required
                    onChange={setSelectedPhone}
                    onValidation={onPhoneNumberValidation}
                    validateOnChange={true}
                  />
                </FormField>
              </div>
            </>
          ) : null}
          {emailSelected ? (
            <>
              {accountEmail ? (
                <div className="notification-layout-slot notification-layout-slot--email-toggle">
                  <CheckboxField
                    id="bhg-use-account-email"
                    checked={selectedEmail === accountEmail}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedEmail(accountEmail);
                        onEmailValidation(true);
                      } else {
                        setSelectedEmail("");
                      }
                    }}
                    label={`Use my account email (${accountEmail})`}
                    disabled={saving}
                  />
                </div>
              ) : null}
              <div className="notification-layout-slot notification-layout-slot--email-input">
                <FormField label="Notification email" htmlFor="dashboard-notification-email">
                  <EmailInput
                    id="dashboard-notification-email"
                    value={selectedEmail}
                    required
                    onChange={setSelectedEmail}
                    onValidation={onEmailValidation}
                    validateOnChange={true}
                  />
                </FormField>
              </div>
            </>
          ) : null}
        </div>
        <CheckboxField
          id="bhg-notification-consent"
          checked={consentToMessages}
          onChange={onConsentChanged}
          label={
            <>
              I consent to receive recurring automated BHG schedule update text messages and/or
              emails from ConstructorFleet at the phone number and/or email provided. Consent is not
              a condition of purchase or use of the BHG Check-in Assist service. Standard Message
              and Data Rates may apply. Reply STOP to opt out of SMS, HELP for help. See our{" "}
              <Link to="/privacy">Privacy Policy</Link> and{" "}
              <Link to="/terms">Terms &amp; Conditions</Link>.
            </>
          }
        />
        {onNotificationSettingsUpdated ? (
          <div className="ds-row">
            <Button
              type="button"
              disabled={!hasChanged || saving}
              onClick={() => void handleSave()}
            >
              {saving ? "Saving..." : "Update notifications"}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
